import { useState } from "react";
import { motion } from "motion/react";
import { Calendar, Clock, Package, Check, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import type { ExecutionPlan } from "@/types/agent-events";
import { fmtVolume } from "@/lib/utils";

interface ExecutionPlanEditModeProps {
  plan: ExecutionPlan;
  onSave: (modifiedPlan: ExecutionPlan) => void;
  onCancel: () => void;
  isSaving?: boolean;
}

export function ExecutionPlanEditMode({
  plan,
  onSave,
  onCancel,
  isSaving = false,
}: ExecutionPlanEditModeProps) {
  // Local state for editable fields
  const [editedDate, setEditedDate] = useState(plan.inputs_normalized.date || "");
  const [editedFirstTime, setEditedFirstTime] = useState(
    plan.inputs_normalized.first_truck_time || 
    plan.inputs_normalized.ouvrages?.[0]?.heure || 
    "08:00"
  );
  const [editedOuvrages, setEditedOuvrages] = useState<Array<{ name: string; volume: number; formula?: string; heure?: string }>>(
    plan.inputs_normalized.ouvrages?.map(o => ({
      name: o.name,
      volume: typeof o.volume === 'number' ? o.volume : parseFloat(o.volume) || 0,
      formula: o.formula,
      heure: o.heure,
    })) || []
  );

  const handleOuvrageVolumeChange = (index: number, newVolume: string) => {
    setEditedOuvrages(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], volume: parseFloat(newVolume) || 0 };
      return updated;
    });
  };

  const handleOuvrageTimeChange = (index: number, newTime: string) => {
    setEditedOuvrages(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], heure: newTime };
      return updated;
    });
  };

  const handleSave = () => {
    // Calculate total volume from ouvrages
    const totalVolume = editedOuvrages.reduce((sum, o) => sum + (o.volume || 0), 0);
    
    // Create modified plan
    const modifiedPlan: ExecutionPlan = {
      ...plan,
      inputs_normalized: {
        ...plan.inputs_normalized,
        date: editedDate,
        first_truck_time: editedFirstTime,
        total_volume: totalVolume,
        ouvrages: editedOuvrages.map(o => ({
          name: o.name,
          formula: o.formula || "Non spécifiée",
          heure: o.heure || "08:00",
          volume: o.volume,
        })),
      },
      computed_values: {
        ...plan.computed_values,
        truck_count: Math.ceil(totalVolume / 8), // Approximate truck capacity
      },
    };
    
    onSave(modifiedPlan);
  };

  const formatDateDisplay = (dateStr: string): string => {
    try {
      return format(parseISO(dateStr), "EEEE d MMMM", { locale: fr });
    } catch {
      return dateStr;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      className="mb-lg"
    >
      <Card className="border border-primary/30 bg-primary/5">
        <CardHeader className="px-lg">
          <div className="flex items-center gap-sm.5">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <Package className="w-4 h-4 text-primary-foreground" />
            </div>
            <CardTitle className="text-body-md sm:text-subtitle">
              Modifier le plan
            </CardTitle>
          </div>
        </CardHeader>

        <CardContent className="px-lg space-y-lg">
          {/* Date */}
          <div className="space-y-sm">
            <Label className="text-body-md flex items-center gap-sm">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              Date de livraison
            </Label>
            <Input
              type="date"
              value={editedDate}
              onChange={(e) => setEditedDate(e.target.value)}
              className="text-body-md"
            />
            {editedDate && (
              <p className="text-helper text-muted-foreground">
                {formatDateDisplay(editedDate)}
              </p>
            )}
          </div>

          {/* First truck time (for truck reservations) */}
          {plan.intent === "reserve_trucks" && (
            <div className="space-y-sm">
              <Label className="text-body-md flex items-center gap-sm">
                <Clock className="w-4 h-4 text-muted-foreground" />
                Heure premier camion
              </Label>
              <Input
                type="time"
                value={editedFirstTime}
                onChange={(e) => setEditedFirstTime(e.target.value)}
                className="text-body-md"
              />
            </div>
          )}

          {/* Ouvrages */}
          {editedOuvrages.length > 0 && (
            <div className="space-y-md">
              <Label className="text-body-md">Ouvrages</Label>
              {editedOuvrages.map((ouvrage, idx) => (
                <div 
                  key={idx} 
                  className="p-md rounded-lg bg-background/80 border border-border/50 space-y-sm"
                >
                  <p className="text-body-md">{ouvrage.name}</p>
                  <div className="flex gap-md">
                    <div className="flex-1">
                      <Label className="text-helper text-muted-foreground">Volume (m³)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        value={ouvrage.volume}
                        onChange={(e) => handleOuvrageVolumeChange(idx, e.target.value)}
                        className="text-body-md h-8 mt-1"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-helper text-muted-foreground">Heure</Label>
                      <Input
                        type="time"
                        value={ouvrage.heure || ""}
                        onChange={(e) => handleOuvrageTimeChange(idx, e.target.value)}
                        className="text-body-md h-8 mt-1"
                      />
                    </div>
                  </div>
                  {ouvrage.formula && ouvrage.formula !== "Non spécifiée" && (
                    <p className="text-helper text-muted-foreground">
                      Formule: {ouvrage.formula}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Summary */}
          <div className="p-2.5 rounded-lg bg-muted/50 border border-border/30">
            <div className="flex items-center justify-between text-body-md">
              <span className="text-muted-foreground">Volume total:</span>
              <span className="font-semibold">
                {fmtVolume(editedOuvrages.reduce((sum, o) => sum + (o.volume || 0), 0))} m³
              </span>
            </div>
            <div className="flex items-center justify-between text-body-md mt-1">
              <span className="text-muted-foreground">Camions estimés:</span>
              <span className="font-semibold">
                {Math.ceil(editedOuvrages.reduce((sum, o) => sum + (o.volume || 0), 0) / 8)}
              </span>
            </div>
          </div>
        </CardContent>

        <CardFooter className="px-lg gap-sm">
          <Button
            onClick={handleSave}
            size="sm"
            className="flex-1 h-9 gap-1.5"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span className="animate-spin">⏳</span>
                Enregistrement...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Sauvegarder
              </>
            )}
          </Button>
          
          <Button
            onClick={onCancel}
            variant="outline"
            size="sm"
            className="h-9 gap-1.5"
            disabled={isSaving}
          >
            <X className="w-4 h-4" />
            Annuler
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}
